import { MantleStakingAbi } from '../abis/mantle'
import { TotalSupplyAbi } from '../abis/shared'
import type { LstRegistryEntry } from '../registry'
import { ONE_E18, toBigInt, type LstReader } from './shared'

/**
 * Mantle mETH reader.
 *
 * Two calls: `mETH.totalSupply()` + `Staking.mETHToETH(1e18)`. The
 * staking contract quotes mETH→ETH at the current rate; calling with
 * `1e18` returns the 1e18-scaled rate directly.
 *
 * Fallback: when `readerExtras.mantleStaking` is absent, surface
 * totalSupply with a 1:1 placeholder rate.
 */
export const readerMantleMEth = (entry: LstRegistryEntry): LstReader => {
  const staking = entry.readerExtras?.mantleStaking
  if (!staking) {
    return {
      calls: [{ address: entry.address, name: 'totalSupply', params: [] }],
      abis: [TotalSupplyAbi],
      parse: ([supply]) => {
        const totalSupply = toBigInt(supply)
        if (totalSupply === undefined) return undefined
        return {
          totalAssets: totalSupply,
          totalSupply,
          exchangeRate: ONE_E18,
        }
      },
    }
  }
  return {
    calls: [
      { address: entry.address, name: 'totalSupply', params: [] },
      { address: staking, name: 'mETHToETH', params: [ONE_E18] },
    ],
    abis: [TotalSupplyAbi, MantleStakingAbi],
    parse: ([supply, rate]) => {
      const totalSupply = toBigInt(supply)
      const exchangeRate = toBigInt(rate)
      if (totalSupply === undefined || exchangeRate === undefined) {
        return undefined
      }
      return {
        totalAssets: (totalSupply * exchangeRate) / ONE_E18,
        totalSupply,
        exchangeRate,
      }
    },
  }
}
